/**
 First panel in Overview showing current printing status
 */
import React, {Component} from 'react';
import {backendSocketUrl} from "../modules/configs";
import PrintsItem from '../components/prints-item';
import LoadingAnimation from '../components/loading-animation';
import {connect} from 'react-redux';
import {
    getPrinterList, setupBarUpdater, updatePrinterState,
    updateToggleListOfPrinterStatus
} from "../actions/getPrinterStatus";
import {changeDateFilter, startFetchingPrintedModelsWithinSpanThenAppendOrOverride} from '../actions/getPrintedModels';
import {bindActionCreators} from "redux";
import {startFetchingMaterialUsageOfPrinter, startFetchingPrintsNumber} from "../actions/getPrinterStatistics";

export const PrinterStatusTypes = {
    SETUP: 'setup',
    ONLINE: 'online',
    START: 'start',
    PAUSE: 'pause',
    RESUME: 'resume',
    END: 'end',
    OFFLINE: 'offline',
    CANCEL: 'cancel'
}

export const DisplayedStatusByPrinterStatusType = {
    [PrinterStatusTypes.ONLINE]: 'Online',
    [PrinterStatusTypes.START]: 'Printing',
    [PrinterStatusTypes.PAUSE]: 'Paused',
    [PrinterStatusTypes.RESUME]: 'Printing',
    [PrinterStatusTypes.END]: 'Completed',
    [PrinterStatusTypes.OFFLINE]: 'Offline',
    [PrinterStatusTypes.CANCEL]: 'Cancelled'
}

class CurrentStatusSection extends Component {

    static socket;
    static barUpdater;
    static printerId;
    static reconnectIntervalInMs;
    static userId;

    // static pingTimer;

    constructor(props) {
        super(props);
        this.reconnectIntervalInMs = 3 * 1000;
        this.userId = this.props.userProfile['sub'];
        const printersLoaded = this.props.getPrinterList();
        this.initStatusItemToggleList(printersLoaded);
        this.setupBarUpdater();
        this.setupSocket(printersLoaded);
    }

    componentWillUnmount() {
        clearInterval(this.barUpdater);
        // clearInterval(this.pingTimer);
    }

    initStatusItemToggleList(printersLoaded) {
        printersLoaded.then(() => {
            const tempPrinterStatusItemToggleList = [...this.props.togglesOfPrinterStatusItems];
            for (let id in this.props.printerIds) {
                tempPrinterStatusItemToggleList.push(false);
            }
            this.props.updateToggleListOfPrinterStatus(tempPrinterStatusItemToggleList);
        })
    }

    setupBarUpdater() {
        if (this.barUpdater) {
            clearInterval(this.barUpdater);
        }

        this.barUpdater = setInterval(() => {
            this.props.printerIds.forEach(printerId => {
                let oldState = this.props[printerId];
                if (oldState.isPaused) return;
                if (oldState.timeLeft <= 0) {
                    return this.updatePrinterState(printerId, {
                        status: PrinterStatusTypes.END,
                        isPaused: true
                    });
                }
                this.updatePrinterState(printerId, {
                    timeElapsed: oldState.timeElapsed + 1,
                    timeLeft: oldState.timeLeft - 1
                });
            });
        }, 1000);
    }

    // setupPingTimer() {
    //   if (this.pingTimer) {
    //     clearInterval(this.pingTimer);
    //   }
    //
    //   this.pingTimer = setInterval(() => {
    //     console.log("sending ping...");
    //     this.socket.send("ping");
    //   }, 5 * 1000)
    // }

    setupSocket(printersLoaded) {
        this.socket = new WebSocket(backendSocketUrl);
        this.socket.onopen = (e) => {
            this.setupSocketEventListeners();
            this.setupSocketRegistration(this.userId);
            this.getCachedStatus(printersLoaded, this.userId);
        };
    }

    setupSocketEventListeners() {
        this.socket.onmessage = (e) => {
            try {
                let messageJson = JSON.parse(e.data);
                let event = messageJson.event;
                let payload = messageJson.payload;
                this.handleEvent(event, payload);
                console.log('server response -', messageJson);
            }
            catch (error) {
                // not JSON format
                console.log(`message: ${e.data}`);
            }

        };

        this.socket.onclose = (e) => {
            if (e.code) {
                if (e.code === 1000) {
                    console.log('web socket closed.');
                }
                else {
                    this.reconnect();
                }
            }
        };

        this.socket.onerror = (e) => {
            if (e.code === 'ECONNREFUSED') {
                this.reconnect();
            }
        }
    }

    reconnect() {
        setTimeout(() => {
            this.setupSocket();
            console.error("socket connection lost, reconnecting...");
        }, this.reconnectIntervalInMs)
    }

    setupSocketRegistration(userId) {
        this.socket.send(JSON.stringify({
            event: PrinterStatusTypes.SETUP,
            payload: {
                socketType: 'web',
                userId: userId
            }
        }));

    }

    getCachedStatus(printersLoaded, userId) {
        if (!printersLoaded) {
            if (!this.props.printerIds) {
                return console.error('ERR: failed to read printer list');
            }

            printersLoaded = Promise.resolve();
        }

        // TODO: request cache using compositeScope rather than printerId!
        printersLoaded.then(() => {
            this.props.printerIds.forEach((printerId) => {
                this.socket.send(JSON.stringify({
                    event: 'web request status cache',
                    payload: {
                        printerId: printerId,
                        userId: userId
                    }
                }));
            })
        })
            .catch((error) => console.log(error));
    }

    handleEvent(event, payload) {
        if (event === 'web status change') {
            let printerId = payload.printerId;
            this.synchronize(printerId, payload);
            this.handleStatusChange(printerId, payload);
        }
        else if (event === 'get status cache') {
            this.setStatusFromCache(payload);
            this.handleStatusChange(payload.cache.printerId, payload.cache);
        }
    }

    setStatusFromCache(payload) {
        let cache = payload.cache;
        console.log('set status by cache:', cache);
        if (cache.status === PrinterStatusTypes.START || cache.status === PrinterStatusTypes.RESUME) {
            this.updateCachedTime(cache);
        }
        const {status, timeElapsed, timeLeft, printerId} = cache;
        const newState = {
            status,
            timeElapsed,
            timeLeft
        };
        if (cache.jobData) {
            newState.jobData = cache.jobData;
        }
        this.updatePrinterState(printerId, newState);
    }

    updateCachedTime(cache) {
        let timeDifferenceFromCacheToNow = Math.floor((Date.now() - cache.timeStamp) / 1000);
        if (cache.timeLeft > timeDifferenceFromCacheToNow) {
            cache.timeElapsed += timeDifferenceFromCacheToNow;
            cache.timeLeft -= timeDifferenceFromCacheToNow;
            cache.status = PrinterStatusTypes.RESUME;
        }
        else {
            cache.timeElapsed += cache.timeLeft;
            cache.timeLeft = 0;
            cache.status = PrinterStatusTypes.END;
        }
    }

    handleStatusChange(printerId, payload) {
        let newStatus = payload.status;
        if (payload && payload.versionNumber) {
            this.checkVersionNumber(printerId, payload.versionNumber, newStatus);
        }
        if (newStatus === PrinterStatusTypes.START) {
            this.handleStart(printerId);
        }
        else if (newStatus === PrinterStatusTypes.PAUSE) {
            this.handlePause(printerId);
        }
        else if (newStatus === PrinterStatusTypes.RESUME) {
            this.handleResume(printerId);
        }
        else if (newStatus === PrinterStatusTypes.END) {
            this.handleEnd(printerId);
        }
        else if (newStatus === PrinterStatusTypes.OFFLINE) {
            this.handleOffline(printerId);
        }
        else if (newStatus === PrinterStatusTypes.ONLINE) {
            this.handleOnline(printerId);
        }
        else if (newStatus === PrinterStatusTypes.CANCEL) {
            this.handleCancel(printerId);
        }
    }

    synchronize(printerId, payload) {
        let newPayload = {};
        if (payload.timeElapsed !== undefined) {
            newPayload.timeElapsed = Number(payload.timeElapsed);
        }
        if (payload.timeLeft !== undefined) {
            newPayload.timeLeft = Number(payload.timeLeft);
        }
        if (payload.jobData) {
            newPayload.jobData = payload.jobData;
        }
        this.updatePrinterState(printerId, newPayload);
    }

    handleStart(printerId) {
        this.updatePrinterState(printerId, {
            status: PrinterStatusTypes.START,
            isPaused: false
        });
    }

    updatePrinterState(printerId, valueByFieldName) {
        this.props.updatePrinterState({
            [printerId]: valueByFieldName
        })
    }

    handlePause(printerId) {
        this.updatePrinterState(printerId, {
            status: PrinterStatusTypes.PAUSE,
            isPaused: true
        });
    }

    handleResume(printerId) {
        this.updatePrinterState(printerId, {
            status: PrinterStatusTypes.RESUME,
            isPaused: false
        });
    }

    handleEnd(printerId) {
        this.updatePrinterState(printerId, {
            status: PrinterStatusTypes.END,
            isPaused: true
        });
        this.updateRecordList();
        this.updateStatistics(printerId);
    }

    updateRecordList() {
        setTimeout(() => {
            this.props.startFetchingPrintedModelsWithinSpanThenAppendOrOverride(Date.now(), null, false);
            console.log("updating record list...");
        }, 2000);
    }

    updateStatistics(printerId) {
        setTimeout(() => {
            this.props.startFetchingMaterialUsageOfPrinter(printerId);
            this.props.startFetchingPrintsNumber(printerId)
        }, 2000);
    }

    handleOffline(printerId) {
        const prevStatus = this.props[printerId].status;
        if (prevStatus === PrinterStatusTypes.END) {
            this.handleEnd(printerId);
        }
        else if (prevStatus === PrinterStatusTypes.CANCEL) {
            this.handleCancel(printerId);
        }
        else {
            this.updatePrinterState(printerId, {
                status: PrinterStatusTypes.OFFLINE,
                isPaused: true
            });
        }
    }

    handleOnline(printerId) {
        this.updatePrinterState(printerId, {status: PrinterStatusTypes.ONLINE});
        if (!this.props.printerIds.includes(printerId)) {
            window.location.reload();
        }
    }

    handleCancel(printerId) {
        this.updatePrinterState(printerId, {
            status: PrinterStatusTypes.CANCEL,
            isPaused: true
        });
        this.updateRecordList();
        this.updateStatistics(printerId);
    }

    checkVersionNumber(printerId, versionNumber, newStatus) {
        if (newStatus === PrinterStatusTypes.ONLINE) {
            console.log(printerId + ' firmware version number: ' + versionNumber);
        }
    }

    render() {
        return (
            <div>{this.getContentToDisplay()}</div>
        );
    }

    getContentToDisplay() {
        if (this.props.loading) {
            return <LoadingAnimation/>
        } else if (this.props.error) {
            return <div>Failed to fetch printer list</div>
        } else {
            return this.getStatusItems();
        }
    }

    getStatusItems() {
        let itemStack = [];
        this.props.printerIds.forEach((printerId, index) => {
            const statusItem = this.composeStatusItemComponent(printerId, index);
            if (!statusItem.props.hidden) {
                itemStack.push(statusItem);
            }
        });
        return itemStack.length === 0 ? <span>No Activity</span> : itemStack;
    }

    composeStatusItemComponent(printerId, index) {
        const {openLightbox} = this.props;
        const printerState = this.props[printerId];
        const hidden = printerState.status === PrinterStatusTypes.END
            || printerState.status === PrinterStatusTypes.CANCEL
            || printerState.status === PrinterStatusTypes.ONLINE
            || printerState.status === PrinterStatusTypes.OFFLINE // hide also when offline
        const {status, timeElapsed, timeLeft, jobData} = printerState;
        const {jobId} = jobData;
        const previewFileName = `${printerId}@${this.userId}@${jobId}.png`;

        return (
            <PrintsItem
                key={printerId}
                displayedStatus={DisplayedStatusByPrinterStatusType[status]}
                status={status}
                timeElapsed={timeElapsed}
                timeLeft={timeLeft}
                {...jobData}
                index={index}
                expanded={this.props.togglesOfPrinterStatusItems[index]}
                hidden={hidden}
                isStatusItem
                previewFileName={previewFileName}
                openLightbox={openLightbox}
            />
        );
    }
}

const mapStateToProps = state => ({
    ...state.printerStatus,
    userProfile: state.userProfile.profile
});
const mapDispatchToProps = dispatch => {
    return bindActionCreators({
        getPrinterList,
        updatePrinterState,
        startFetchingPrintedModelsWithinSpanThenAppendOrOverride,
        updateToggleListOfPrinterStatus,
        startFetchingMaterialUsageOfPrinter,
        startFetchingPrintsNumber
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(CurrentStatusSection);




// WEBPACK FOOTER //
// ./src/containers/current_status_section.js